'use client'

import faceImg from '@/assets/face.jpg'
import Container from '@/components/Container'
import { Button } from '@/components/ui/button'
import { MenuIcon, PenToolIcon, XIcon } from 'lucide-react'
import { AnimatePresence, m } from 'motion/react'
import Image from 'next/image'
import Link from 'next/link'
import { useState } from 'react'

const NAV_LINKS = [
  { label: 'Profile', href: '#profile' },
  { label: 'Experience', href: '#works' },
  { label: 'Skills', href: '#skills' },
  { label: 'Education', href: '#education' },
]

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false)

  return (
    <m.header
      className="w-full fixed top-0 z-50"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
    >
      <Container>
        <div className={'bg-background pt-5 rounded-b-2xl -mx-px'}>
          <nav
            className={`border shadow-[0_6px_12px_rgba(29,29,38,0.04)] bg-white p-1.5 rounded-2xl flex items-center justify-between gap-4`}
          >
            {/* Brand */}
            <Link
              href="#home"
              className="text-sm font-semibold text-foreground flex items-center gap-3"
              style={{ fontFamily: 'var(--font-playwrite)' }}
            >
              <Image
                className={'rounded-xl overflow-hidden size-10'}
                src={faceImg}
                alt={'Robert Siennicki'}
                width={120}
                height={120}
              />
              <span>R.Siennicki</span>
            </Link>

            {/* Desktop nav links */}
            <ul className="hidden items-center sm:flex">
              {NAV_LINKS.map(({ label, href }) => (
                <li key={label}>
                  <Button
                    asChild
                    variant={'link'}
                    className={'hover:bg-[#f6f6f6] hover:no-underline'}
                  >
                    <Link
                      href={href}
                      className="text-sm font-medium text-muted-foreground transition-colors"
                    >
                      {label}
                    </Link>
                  </Button>
                </li>
              ))}
            </ul>

            {/* Right controls */}
            <div className="flex items-center gap-3">
              <Button
                size="lg"
                className="rounded-xl hidden sm:flex"
                asChild
              >
                <Link href="/#contact">
                  <PenToolIcon className="size-3.5" />
                  Contact Me
                </Link>
              </Button>

              {/* Mobile hamburger */}
              <Button
                size="icon-lg"
                className="sm:hidden"
                onClick={() => setMenuOpen((o) => !o)}
                aria-label="Toggle menu"
              >
                {menuOpen ? <XIcon className="h-5 w-5" /> : <MenuIcon className="h-5 w-5" />}
              </Button>
            </div>
          </nav>
        </div>

        {/* Mobile dropdown */}
        <AnimatePresence>
          {menuOpen && (
            <m.div
              className="absolute inset-x-5 top-full mt-2 border shadow-[0_6px_12px_rgba(29,29,38,0.04)] bg-white p-1.5 rounded-2xl sm:hidden overflow-hidden"
              initial={{ opacity: 0, y: -8, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.97 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
            >
              <m.ul
                className="flex flex-col gap-1"
                initial="hidden"
                animate="visible"
                variants={{
                  hidden: {},
                  visible: { transition: { staggerChildren: 0.05, delayChildren: 0.05 } },
                }}
              >
                {NAV_LINKS.map(({ label, href }) => (
                  <m.li
                    key={label}
                    variants={{
                      hidden: { opacity: 0, x: -8 },
                      visible: { opacity: 1, x: 0 },
                    }}
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                  >
                    <Link
                      href={href}
                      onClick={() => setMenuOpen(false)}
                      className="block rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    >
                      {label}
                    </Link>
                  </m.li>
                ))}
                <m.li
                  className={'mt-2'}
                  variants={{
                    hidden: { opacity: 0, x: -8 },
                    visible: { opacity: 1, x: 0 },
                  }}
                  transition={{ duration: 0.2, ease: 'easeOut' }}
                >
                  <Button
                    size="lg"
                    className="w-full"
                    asChild
                  >
                    <Link
                      href="/#contact"
                      onClick={() => setMenuOpen(false)}
                    >
                      <PenToolIcon className="size-3.5" />
                      Contact Me
                    </Link>
                  </Button>
                </m.li>
              </m.ul>
            </m.div>
          )}
        </AnimatePresence>
      </Container>
    </m.header>
  )
}
